/** Platform-specific limits, rules, and media specifications */
export const PLATFORM_LIMITS = {
  INSTAGRAM: {
    chars: 2200,
    hashtags: 30,
    linksClickable: false,
    addLinkInBio: true,
    linkInBioText: '\n\n🔗 Link in bio',
    mediaSpecs: {
      // A feed image may be anything from 4:5 portrait to 1.91:1 landscape
      // (width / height 0.8–1.91); it is not cropped to a square.
      feed: { width: 1080, minAspect: 0.8, maxAspect: 1.91, ratio: '4:5 to 1.91:1', maxMB: 8 },
      story: { width: 1080, height: 1920, ratio: '9:16', maxSeconds: 60 },
      reel: { width: 1080, height: 1920, ratio: '9:16', maxSeconds: 900 },
      // Meta raised the carousel to 20 items (Content Publishing API, 2024).
      carousel: { minItems: 2, maxItems: 20 },
    },
    // A caption may tag at most 20 accounts (@handle); more and the publish is refused.
    mentions: 20,
  },
  LINKEDIN: {
    chars: 3000,
    hashtags: 5,
    linksClickable: true,
    // LinkedIn penalises posts with >5 hashtags — algorithm reduces reach by ~40%
    penaliseHashtagSpam: true,
    imageSpec: { width: 1200, height: 627 },
  },
  TWITTER: {
    // A weighted length, not characters: see x-text.ts (CJK and emoji
    // weigh 2, every URL 23).
    chars: 280,
    linksClickable: true,
    // Every URL counts as 23 chars regardless of actual length
    linkCounts: 23,
    // Break threads leaving room for numbering (weighted, like chars)
    threadBreakAt: 270,
    imageSpec: { width: 1200, height: 675 },
  },
  FACEBOOK: {
    chars: 63206,
    hashtags: null,
    linksClickable: true,
    autoLinkPreview: true,
    imageSpec: { width: 1200, height: 630 },
  },
  WHATSAPP: {
    chars: 1024,
    hashtags: 0,
    templatesOnly: true,
    linksClickable: true,
  },
  TIKTOK: {
    // 2,200 is the Content Posting API caption ceiling. Hashtags count
    // inside it rather than as a separate field, so the number below is
    // guidance for the composer, not a second budget.
    chars: 2200,
    // A photo post's description is longer: 4,000 UTF-16 units
    // (TIKTOK_PHOTO_DESCRIPTION_MAX, tiktok-text.ts).
    photoDescChars: 4000,
    hashtags: 30,
    // Captions are not linkified for ordinary accounts, so the composer
    // must not promise a clickable link.
    linksClickable: false,
    videoSpec: {
      portrait: { width: 1080, height: 1920, maxSeconds: 600 },
    },
  },
  YOUTUBE: {
    titleChars: 100,
    // The description's real limit is 5,000 BYTES of UTF-8 (descBytes); the
    // character budget is only the composer's first guide. A Malayalam or
    // Arabic description reaches the byte limit well before 5,000 characters.
    descChars: 5000,
    descBytes: 5000,
    // snippet.tags: 500 characters for the whole list, not a count of tags.
    tagsChars: 500,
    videoSpec: {
      landscape: { width: 1920, height: 1080 },
      // Shorts may run up to 3 minutes since 15 October 2024.
      shorts: { width: 1080, height: 1920, maxSeconds: 180 },
    },
  },
  TELEGRAM: {
    chars: 4096,
    hashtags: null,
    linksClickable: true,
    supportsHtmlFormatting: true,
    // A caption under a photo, video or album is capped at 1,024
    // characters; a text message at 4,096.
    captionChars: 1024,
    // sendMediaGroup takes 2–10 photos or videos.
    mediaGroupMax: 10,
    // Telegram fetches a file sent by URL itself, up to 5 MB for a photo and
    // 20 MB for anything else. Larger files must be uploaded (multipart),
    // which the Bot API takes up to 50 MB.
    urlPhotoMaxMB: 5,
    urlFileMaxMB: 20,
    uploadMaxMB: 50,
  },
  PINTEREST: {
    titleChars: 100,
    descChars: 500,
    requiresDestinationUrl: true,
    minImageSize: { width: 100, height: 200 },
  },
  /**
   * Zetpin (zetpin.com), the hyperlocal network: 5,000 characters counted in
   * graphemes, up to 50 hashtags, clickable links, up to 10 images, and an
   * optional location so the post reaches people nearby (zetpin.ts).
   */
  ZETPIN: {
    chars: 5000,
    hashtags: 50,
    linksClickable: true,
    supportsHyperlocal: true,
    maxImages: 10,
    imageSpec: { width: 1080, height: 1080 },
  },
  /**
   * Google Business Profile posts on the Search and Maps listing. 1,500
   * characters, links shown as a call-to-action button rather than clickable
   * in the text, one photo. Hashtags do nothing on Google and are not counted.
   */
  GOOGLE_BUSINESS: {
    chars: 1500,
    hashtags: 0,
    linksClickable: false,
    maxImages: 1,
    imageSpec: { width: 1200, height: 900, aspect: '4:3' },
  },
} as const;

export type PlatformLimitsMap = typeof PLATFORM_LIMITS;
